// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    productNumber?: string;
    customerProductNumber?: string;
    manufacturerItem?: string;
    showCustomerName?: boolean;
    showProductNumber?: boolean;
    showPartNumber?: boolean;
    showManufacturerItem?: boolean;
    extendedStyles?: ProductPartNumbersStyles;
    isProductDetailsPage?: boolean;
    upcCode?: string;
    brandName?: string;

    // this option is not needed but removing it for now to avoid build errors
    showLabel?: boolean;
}

type Props = OwnProps;

export interface ProductPartNumbersStyles {
    condensed?: boolean;
    container?: InjectableCss;
    erpNumberGridItem?: InjectableCss;
    erpNumberLabelText?: TypographyPresentationProps;
    erpNumberValueText?: TypographyPresentationProps;
    customerNameGridItem?: InjectableCss;
    customerNameLabelText?: TypographyPresentationProps;
    customerNameValueText?: TypographyPresentationProps;
    manufacturerItemGridItem?: InjectableCss;
    manufacturerItemLabelText?: TypographyPresentationProps;
    manufacturerItemValueText?: TypographyPresentationProps;
    brandGridItem?: InjectableCss;
    brandLabelText?: TypographyPresentationProps;
    brandValueText?: TypographyPresentationProps;
    upcCodeGridItem?: InjectableCss;
    upcCodeLabelText?: TypographyPresentationProps;
    upcCodeValueText?: TypographyPresentationProps;
}

const sharedNumberGridStyle = `
    flex: 1;
    line-height: 20px;
    font-size: 13px;
    height: 20px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding-right: 10px;

    @media (max-width: 450px) {
        overflow: visible;
        text-overflow: unset;
    }
`;

const sharedLabelAndTextStyle = (bold?: boolean) =>
    ({
        size: "13px",
        lineHeight: "20px",
        weight: bold ? "bold" : "lighter",
    } as TypographyPresentationProps);

export const productPartNumbersStyles: ProductPartNumbersStyles = {
    container: {
        css: css`
            flex: 1;
            display: flex;
            justify-content: flex-start;
            @media (max-width: 450px) {
                flex-wrap: wrap;
            }
        `,
    },
    manufacturerItemGridItem: {
        css: css`
            ${sharedNumberGridStyle}
        `,
    },
    erpNumberGridItem: {
        css: css`
            ${sharedNumberGridStyle}
        `,
    },
    customerNameGridItem: {
        css: css`
            ${sharedNumberGridStyle}
        `,
    },
    brandGridItem: {
        css: css`
            ${sharedNumberGridStyle}
        `,
    },
    upcCodeGridItem: {
        css: css`
            ${sharedNumberGridStyle}
        `,
    },
    erpNumberLabelText: sharedLabelAndTextStyle(true),
    customerNameLabelText: sharedLabelAndTextStyle(true),
    manufacturerItemLabelText: sharedLabelAndTextStyle(true),
    brandLabelText: sharedLabelAndTextStyle(true),
    upcCodeLabelText: sharedLabelAndTextStyle(true),
    erpNumberValueText: sharedLabelAndTextStyle(),
    customerNameValueText: sharedLabelAndTextStyle(),
    manufacturerItemValueText: sharedLabelAndTextStyle(),
    brandValueText: sharedLabelAndTextStyle(),
    upcCodeValueText: sharedLabelAndTextStyle(),
};

const ProductPartNumbers: FC<Props> = ({
    productNumber,
    customerProductNumber,
    manufacturerItem,
    showCustomerName = false,
    showManufacturerItem = true,
    showPartNumber = true,
    extendedStyles,
    showProductNumber = true,
    isProductDetailsPage = false,
    upcCode,
    brandName,
}) => {
    const [styles] = React.useState(() => mergeToNew(productPartNumbersStyles, extendedStyles));
    // on PDP, if CPN is available we are displaying it instead of the productNumber
    const displayPartNumber = showProductNumber && showPartNumber && productNumber && (isProductDetailsPage ? !customerProductNumber : true);
    const displayManufacturerNumber = showManufacturerItem && manufacturerItem;
    const displayCustomerNumber = showCustomerName && customerProductNumber;

    return (
        <StyledWrapper {...styles.container}>
            {displayManufacturerNumber && (
                <StyledWrapper {...styles.manufacturerItemGridItem}>
                    <Typography {...styles.manufacturerItemLabelText}>{translate("MFR Part #:")}&nbsp;</Typography>
                    <VisuallyHidden>
                        <Typography {...styles.manufacturerItemLabelText}>{translate("MFR Part #")} </Typography>
                    </VisuallyHidden>
                    <Typography {...styles.manufacturerItemValueText} data-test-selector="manufacturerItem">
                        {manufacturerItem}
                    </Typography>
                </StyledWrapper>
            )}
            {displayPartNumber && (
                <StyledWrapper {...styles.erpNumberGridItem}>
                    <Typography {...styles.erpNumberLabelText}>{translate("SKU #:")}&nbsp;</Typography>
                    <VisuallyHidden>
                        <Typography {...styles.erpNumberLabelText}>{translate("SKU #")}</Typography>
                    </VisuallyHidden>
                    <Typography {...styles.erpNumberValueText} data-test-selector="productNumber">
                        {productNumber}
                    </Typography>
                </StyledWrapper>
            )}
            {displayCustomerNumber && (
                <StyledWrapper {...styles.customerNameGridItem}>
                    <Typography {...styles.customerNameLabelText}>{translate("CPN #:")}&nbsp;</Typography>
                    <VisuallyHidden>
                        <Typography {...styles.customerNameLabelText}>{translate("CPN #")}</Typography>
                    </VisuallyHidden>
                    <Typography {...styles.customerNameValueText} data-test-selector="customerProductNumber">
                        {customerProductNumber}
                    </Typography>
                </StyledWrapper>
            )}
            {isProductDetailsPage && brandName && (
                <StyledWrapper {...styles.brandGridItem}>
                    <Typography {...styles.brandLabelText}>{translate("Brand:")}&nbsp;</Typography>
                    <VisuallyHidden>
                        <Typography {...styles.brandLabelText}>{translate("Brand")}</Typography>
                    </VisuallyHidden>
                    <Typography {...styles.brandValueText}>{brandName}</Typography>
                </StyledWrapper>
            )}
            {isProductDetailsPage && upcCode && (
                <StyledWrapper {...styles.upcCodeGridItem}>
                    <Typography {...styles.upcCodeLabelText}>{translate("UPC:")}&nbsp;</Typography>
                    <VisuallyHidden>
                        <Typography {...styles.upcCodeLabelText}>{translate("UPC")}</Typography>
                    </VisuallyHidden>
                    <Typography {...styles.upcCodeValueText}>{upcCode}</Typography>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default ProductPartNumbers;
